import { H2 } from '@wheely/ui-kit';
import cn from 'classnames';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';

import { noop } from '@/shared/utils/noop';
import { Button } from '@src-common/view/adapters/Button';

import { CenteringContainer } from '../CenteringContainer';

import { Filters, MobileFilters, IFiltersProps } from './Filters';
import { IListProps, List } from './List';
import s from './ListLayout.module.css';
import { IMobileListProps, MobileList } from './MobileList';
import { NoResults } from './NoResults';
import { Pagination } from './Pagination';
import { isEmpty } from './utils';

const MOBILE_VIEW_WIDTH_PX = 419;

interface IListLayoutProps<TEntity, TFilterNames extends string> {
  title: React.ReactNode;
  action?: {
    title: React.ReactNode;
    onClick: () => void;
    className?: string;
  };
  before?: React.ReactNode;
  after?: React.ReactNode;
  data: TEntity[];
  total?: number;
  limit?: number;
  filterConfig?: IFiltersProps<TFilterNames>['config'];
  onClickRow?: IListProps<TEntity>['onClickRow'];
  columns: IListProps<TEntity>['columns'];
  renderItem: IMobileListProps<TEntity>['renderItem'];
  getEntityId: IListProps<TEntity>['getEntityId'];
  onFetchData: (
    filters: Partial<Record<TFilterNames, any>>,
    pagination: { offset: number; limit: number },
  ) => void;
  onFiltersChange?: (filters: Partial<Record<TFilterNames, any>>) => void;
  shouldBeEmptyWithoutFilters: false;
  getEntityGroup?: IMobileListProps<TEntity>['getEntityGroup'];
  groupOrder?: IMobileListProps<TEntity>['groupOrder'];
  translateGroupName?: IMobileListProps<TEntity>['translateGroupName'];
  isFiltersEmpty?: (filters: Partial<Record<TFilterNames, any>>) => boolean;
  isLoading?: boolean;
  wrapperClassName?: string;
}

const ListLayoutView = <TEntity, TFilterNames extends string>({
  title,
  action,
  before,
  after,
  filterConfig,
  onClickRow,
  shouldBeEmptyWithoutFilters,
  data,
  columns,
  getEntityId,
  limit,
  total,
  onFetchData,
  onFiltersChange = noop,
  renderItem,
  translateGroupName,
  getEntityGroup,
  groupOrder,
  isFiltersEmpty = isEmpty,
  isLoading = false,
  wrapperClassName,
}: IListLayoutProps<TEntity, TFilterNames>) => {
  type TFilterValues = Partial<Record<TFilterNames, string | Date>>;

  const filterDefaultValue: TFilterValues = useMemo(
    () =>
      !filterConfig
        ? {}
        : filterConfig.reduce((result, item) => {
            if (item.initialValue) {
              result[item.name] = item.initialValue;
            }

            if (item.fixedValue) {
              result[item.name] = item.fixedValue;
            }

            return result;
          }, {} as TFilterValues),
    [filterConfig],
  );

  const hasPagination = total !== undefined && limit !== undefined;
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState(filterDefaultValue);
  const [isMobileView, setIsMobileView] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);

  const changePage = useCallback((newOffset: number) => {
    setOffset(newOffset);

    setTimeout(() => {
      filtersRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }, 100);
  }, []);

  const handleRemoveFilters = useCallback(() => {
    setFilters(filterDefaultValue);
  }, [filterDefaultValue]);

  const handleChangeFilter: IFiltersProps<TFilterNames>['onChange'] = useCallback(
    (_, { name, value }) => {
      setFilters(params => {
        const newFilters = { ...params, [name]: value };

        onFiltersChange(newFilters);

        return newFilters;
      });
      setOffset(0);
    },
    [onFiltersChange],
  );

  useEffect(() => {
    const handleChangeScreen = (event: MediaQueryListEvent) => {
      setIsMobileView(event.matches ?? false);
    };
    const match = window.matchMedia(`screen and (max-width: ${MOBILE_VIEW_WIDTH_PX}px)`);

    setIsMobileView(match.matches);

    if (match.addEventListener) {
      match.addEventListener('change', handleChangeScreen);
    } else {
      // Safari 13 https://www.designcise.com/web/tutorial/how-to-fix-the-javascript-typeerror-matchmedia-addeventlistener-is-not-a-function
      match.addListener(handleChangeScreen);
    }

    return () => {
      if (match.removeEventListener) {
        match.removeEventListener('change', handleChangeScreen);
      } else {
        match.removeListener(handleChangeScreen);
      }
    };
  }, [setIsMobileView]);

  useEffect(() => {
    if (shouldBeEmptyWithoutFilters && isFiltersEmpty(filters)) {
      return;
    }

    onFetchData(filters, hasPagination ? { limit, offset } : { limit: 100, offset: 0 });
  }, [
    offset,
    filters,
    shouldBeEmptyWithoutFilters,
    limit,
    onFetchData,
    hasPagination,
    isFiltersEmpty,
  ]);

  return (
    <CenteringContainer outerClassName={cn(s.wide, wrapperClassName)}>
      <div className={s.headerRow}>
        <H2 capitalize={true}>{title}</H2>
      </div>
      {before && <div className={s.beforeRow}>{before}</div>}
      {(action || filterConfig) && (
        <div className={s.actionRow}>
          {isMobileView ? (
            <MobileFilters<TFilterNames>
              value={filters}
              config={filterConfig ?? []}
              onChange={handleChangeFilter}
              onRemoveFilters={handleRemoveFilters}
              filtersRef={filtersRef}
            />
          ) : (
            <Filters<TFilterNames>
              value={filters}
              config={filterConfig ?? []}
              onChange={handleChangeFilter}
              actionWrapperClassName={action?.className}
            >
              {action && <Button onClick={action.onClick}>{action.title}</Button>}
            </Filters>
          )}
        </div>
      )}
      <div className={s.listRow}>
        {!isLoading && !isEmpty(filters) && data.length === 0 && (
          <NoResults onRemoveFilters={handleRemoveFilters} />
        )}
        {(isLoading || data.length > 0) && isMobileView && (
          <MobileList<TEntity>
            key="list"
            getEntityId={getEntityId}
            onClickRow={onClickRow}
            data={shouldBeEmptyWithoutFilters && isFiltersEmpty(filters) ? [] : data}
            renderItem={renderItem}
            getEntityGroup={getEntityGroup}
            groupOrder={groupOrder}
            translateGroupName={translateGroupName}
            isLoading={isLoading}
            action={action}
          />
        )}
        {(isLoading || data.length > 0) && !isMobileView && (
          <List
            key="table"
            data={shouldBeEmptyWithoutFilters && isFiltersEmpty(filters) ? [] : data}
            columns={columns}
            getEntityId={getEntityId}
            onClickRow={onClickRow}
            isLoading={isLoading}
          />
        )}
      </div>
      {hasPagination && (
        <div className={s.paginationRow}>
          <Pagination
            offset={offset}
            limit={limit}
            total={total}
            onChangeOffset={changePage}
            isDisabled={isLoading}
          />
        </div>
      )}
      {after && <div className={s.afterRow}>{after}</div>}
    </CenteringContainer>
  );
};

export { ListLayoutView };
